<script>
    import { ServiceConsts } from "$/services/ServiceConsts";
    import { TenantStore } from "$/stores/TenantStore";
    import {
        ActiveTopic,
        TopicAccessLevel,
    } from "$/stores/extension/ActiveTopicStore";
    import { GlobalSearchStore } from "$/stores/GlobalSearchStore";
    import { clickAction } from "$/utils/clickAction";
    import { clickOutside } from "$/utils/clickOutside.js";
    import TagSuggestionList from "../editor/extensions/tag/TagSuggestionList.svelte";
    import { ClusterStore } from "$/stores/ClusterStore";
    import AnimatedBorder from "../animations/AnimatedBorder.svelte";
    import { truncateText } from "$/utils/Utils"

    export let card;
    export let editRole = TopicAccessLevel.OWNER;
    export let tagSearchQuery = "";
    export let openSearch = false;
    export let addTag;
    export let removeTag;
    export let animateOnMount = false;

    let searchTagInput;
    let tagSuggestions;

    const handleTagClick = async (event) => {
        let tag = event.target.innerText.substring(1);

        if (ServiceConsts.isDashboardUrl(window.location.href)) {

            let tagQuery = `#${tag}`;

            GlobalSearchStore.openSearchModal();
            GlobalSearchStore.setSearchQuery(tagQuery);
            GlobalSearchStore.search(
                tagQuery,
                ClusterStore.getAllClusterTopics(),
                $TenantStore?.active?.id,
                $ActiveTopic.snippets,
                false,
            );
        } else {
            window.open(
                `${ServiceConsts.DASHBOARD_URL}/#/${$TenantStore?.active?.id}?q=${tag}`,
                "_blank",
            );
        }
    };

    function handleRemoveTag(tag) {
        removeTag(tag);
    }

    async function addTagToSnippet(tag) {
        tagSearchQuery = "";
        searchTagInput.focus();
        addTag(tag);
    }

    const closeTagSearch = () => {
        openSearch = false;
        tagSearchQuery = "";
    };

    $:tagItems = $ActiveTopic?.tags ?? [];

    $: filteredTagItems = tagItems.filter((item) =>
        item.tag.toLowerCase().startsWith(tagSearchQuery.toLowerCase()),
    );

    $: {
        if (openSearch && searchTagInput) {
            searchTagInput.focus();
        }
    }

    const preventInvalidKeys = (event) => {
        const key = event.key;
        const allowKeys = /^[\p{L}0-9_-]$/u;

        if (
            !allowKeys.test(key) &&
            key !== "Backspace" &&
            key !== "ArrowLeft" &&
            key !== "ArrowRight" &&
            key !== "Delete"
        ) {
            event.preventDefault();
        }

        if (key === " ") tagSearchQuery = tagSearchQuery.replace(/\./g, "");

        if (tagSearchQuery.endsWith(" ")) {
            tagSearchQuery = tagSearchQuery.trimEnd();
        }
    };

</script>

{#if card.tags?.length > 0}
    <div class="tags">
        {#each card.tags as tag}
            <AnimatedBorder
                duration={animateOnMount ? 2000 : null}
                borderRadius="9999px"
            >
                <span
                    class="tag"
                    class:tag__without_delete={editRole <
                        TopicAccessLevel.COMMENTER}
                >
                    <a
                        class="tag__name"
                        href={"#"}
                        class:tag__name_without_delete={editRole <
                            TopicAccessLevel.COMMENTER}
                        on:click={handleTagClick}
                    >
                        #{truncateText(tag.tag,38)} 
                    </a>
                    {#if editRole >= TopicAccessLevel.COMMENTER}
                        <div
                            class="tag__remove"
                            use:clickAction={() =>
                                handleRemoveTag(tag, card.id)}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>
                    {/if}
                </span>
            </AnimatedBorder>
        {/each}
    </div>
{/if}

<div class="tag-search-wrapper">
    {#if openSearch}
        <div class="tag-search">
            <input
                type="text"
                placeholder="Search tags..."
                use:clickOutside={{
                    ignoreSelectors: [".suggestion-list-wrapper"],
                }}
                on:click_outside={closeTagSearch}
                bind:value={tagSearchQuery}
                bind:this={searchTagInput}
                on:keydown={(event) => {
                    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
                        event.preventDefault();
                    }
                    preventInvalidKeys(event);
                    event.stopPropagation();
                    tagSuggestions?.onKeyDown?.({ event });
                }}
            />
        </div>
    {/if}
</div>

{#if tagSearchQuery}
    <span
        class="suggestion-list-wrapper"
        use:clickOutside={{ ignoreSelectors: [".tag-search"] }}
        on:click_outside={closeTagSearch}
    >
        <TagSuggestionList
            props={{
                userRole: editRole,
                items: filteredTagItems,
                command: addTagToSnippet,
                query: tagSearchQuery,
            }}
            bind:this={tagSuggestions}
            isComment={false}
            inverseList={false}
            snippetTag={true} 
        />
    </span>
{/if}

<style>
    @import "../../main.css";

    .tags {
        @apply -my-1 flex shrink-0 flex-wrap px-2 pt-2 !important;
    }

    .tag {
        @apply -mr-4 flex max-w-full flex-row items-center gap-0.5 overflow-hidden rounded-full py-1 pl-2 pr-1 tracking-tight hover:z-10 hover:bg-sky-100 hover:shadow-[10px_0px_0px_rgba(255,255,255,0.9)] !important;
    }

    .tag__name {
        @apply cursor-pointer overflow-clip text-ellipsis text-nowrap text-xs text-sky-500 no-underline transition-colors duration-1000 !important;
    }
    .tag__name--animation {
        @apply text-pink-500 !important;
    }

    .tag__name_without_delete {
        @apply pr-1 !important;
    }

    .tag__without_delete {
        @apply mr-0 !important;
    }

    .tag__remove {
        @apply invisible flex flex-shrink-0 cursor-pointer items-center justify-center !important;
    }

    .tag__remove svg {
        @apply h-4 w-4 text-sky-500 !important;
    }

    .tag:hover .tag__remove {
        @apply visible !important;
    }

    .tag-search {
        @apply relative mt-2 w-full px-2 !important;
    }

    .tag-search input {
        @apply w-full px-2 py-1 text-xs focus:outline-none dark:bg-white dark:text-black !important;
    }

    .tag-search-wrapper {
        @apply relative !important;
    }

    .suggestion-list-wrapper {
        @apply absolute w-full !important;
    }
</style>
